import * as React from 'react';
import { createElement } from 'react';
import { createTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { Box, Icon } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import dynamic from 'next/dynamic';
import { statusProgress } from '..';
import { SalesOrderStatusGroupDto } from 'src/models/salesorder';

const defaultTheme = createTheme();
const useStyles = makeStyles(
  theme =>
    createStyles({
      root: {
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: 26,
        borderRadius: 3
      },
      value: {
        position: 'absolute',
        lineHeight: '24px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }
    }),
  { defaultTheme }
);
interface ProgressBarProps {
  value: string;
  colour: string;
  percentage: number;
  icon: string;
  iconcolour: string;
  noofdiscrep: boolean;
  discrepancyColour: string;
  discrepancyIconColour: string;
  discrepancyIcon: string;
}

const IconRenderer = ({ progressProps }: { progressProps: any }) => {
  let icon = progressProps?.discrepancyIcon ?? '';
  if (icon === '') {
    // @ts-ignore
    icon = dynamic(() => import('mdi-material-ui').then(mod => mod[['AccountCircleOutline']]));
  } else {
    // @ts-ignore
    icon = dynamic(() => import('mdi-material-ui').then(mod => mod[[progressProps.discrepancyIcon]]));
  }

  if (!progressProps.noofdiscrep) {
    return <div></div>;
  } else {
    return (
      <Icon
        sx={{
          height: '1.4rem',
          color: progressProps.discrepancyColour
        }}>
        {createElement(icon)}
      </Icon>
    );
  }
};

const ProgressBar = (props: ProgressBarProps) => {
  const theme = useTheme();

  function contrastingColor(color: any) {
    return luma(color) >= 165 ? '000' : 'fff';
  }

  function luma(color: any) {
    // color can be a hx string or an array of RGB values 0-255
    const rgb = typeof color === 'string' ? hexToRGBArray(color) : color;

    return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]; // SMPTE C, Rec. 709 weightings
  }

  function hexToRGBArray(color: any) {
    color = color.replace('#', '');

    if (color.length === 3) {
      color = color.charAt(0) + color.charAt(0) + color.charAt(1) + color.charAt(1) + color.charAt(2) + color.charAt(2);
    } else if (color.length !== 6) {
      console.log('Invalid HEX');
      console.log(value);
    }
    const rgb = [];
    for (let i = 0; i <= 2; i++) rgb[i] = parseInt(color.substr(i * 2, 2), 16);

    return rgb;
  }

  const { value, percentage, colour, noofdiscrep } = props;

  const classes = useStyles();

  let color: string;
  if (theme.palette.mode === 'light') {
    if (percentage > 25) {
      color = contrastingColor(colour);
    } else {
      color = '000';
    }
  } else {
    if (percentage > 25) {
      color = contrastingColor(colour);
    } else {
      color = 'fff';
    }
  }

  return (
    <Box className={classes.root}>
      {!noofdiscrep && (
        <Box sx={{ color: '#' + color }} className={classes.value}>
          {value}
        </Box>
      )}
      {noofdiscrep && (
        <Box sx={{ color: '#' + color }} className={classes.value}>
          {' '}
          {value}
        </Box>
      )}

      <Box style={{ backgroundColor: colour, height: '100%', maxWidth: `${percentage}%` }}>
        <IconRenderer progressProps={props} />
      </Box>
    </Box>
  );
};

/**
 * Returns an object containing the status progress details for a given sales order status.
 *
 * @param {string} currentStatus - The current sales order status.
 * @param {boolean} noofdiscrep - Does the sales order have discrepancies.
 * @param {SalesOrderStatusGroupDto[]} groups - An array of sales order status groups.
 * @return {statusProgress} An object containing the status progress details.
 */
const getStatusProgress = (
  currentStatus: string,
  noofdiscrep: boolean,
  groups: SalesOrderStatusGroupDto[]
): statusProgress => {
  const statusGrpDetails: SalesOrderStatusGroupDto = groups?.find(obj => obj.description === currentStatus) ?? {};

  const discrepGroup: SalesOrderStatusGroupDto = groups?.find(obj => obj.code === 'Discrep') ?? {};

  const returnObj: statusProgress = {
    value: currentStatus,
    colour: statusGrpDetails.statusColour ?? '',
    percentage: statusGrpDetails.percentage ?? 0,
    iconcolour: statusGrpDetails.iconColour ?? '',
    icon: statusGrpDetails.icon ?? '',
    noofdiscrep: noofdiscrep ?? false,
    discrepancyColour: discrepGroup.iconColour ?? '',
    discrepancyIcon: discrepGroup.icon ?? '',
    discrepancyIconColour: discrepGroup.iconColour ?? ''
  };

  return returnObj;
};

export const RenderProgress = ({
  value,
  numOfDiscrep,
  statusGroups
}: {
  value: string;
  numOfDiscrep?: boolean;
  statusGroups: SalesOrderStatusGroupDto[];
}) => {
  const theme = useTheme();

  const determineBackgroundColour = (status: string) => {
    if (status === 'Order Created') return '';

    if (theme.palette.mode === 'dark') {
      return '#a3a3a3';
    } else {
      return '';
    }
  };

  const params = React.useMemo(
    () => getStatusProgress(value, numOfDiscrep ?? false, statusGroups),
    [numOfDiscrep, statusGroups, value]
  );

  return (
    <Box
      sx={{
        backgroundColor: determineBackgroundColour(value),
        ...(theme.palette.mode === 'dark' && value === 'Order Created' && { border: '1px solid #a3a3a3' })
      }}>
      <ProgressBar
        percentage={params.percentage}
        colour={params.colour}
        value={params.value}
        icon={params.icon}
        iconcolour={params.iconcolour}
        noofdiscrep={params.noofdiscrep}
        discrepancyColour={params.discrepancyColour}
        discrepancyIcon={params.discrepancyIcon}
        discrepancyIconColour={params.discrepancyIconColour}
      />
    </Box>
  );
};
